import React, { FC, useRef } from 'react';
import AddressCheckOfferPlacementStyled from './AddressCheckOfferPlacementStyled';
import { iconMap } from '~/constants/iconMap';
import useMediaQuery from '~/hooks/useMediaQuery';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Pagination, Navigation } from 'swiper/modules';
import { trackEvent } from '~/helpers/tracking';

import 'swiper/css';
import 'swiper/css/pagination';
import 'swiper/css/navigation';
import Tooltip from '../Tooltip';
import { InfoCircle } from '../Icons/outline';

interface Tile {
  heading?: string;
  copy?: string;
  icon?: string;
  tooltip?: string;
}

interface Props {
  title?: string;
  tiles?: Tile[];
}

const AddressCheckOfferPlacement: FC<Props> = ({ title, tiles }) => {
  const swiperRef = useRef(null);
  const isMobile = useMediaQuery('(max-width: 767px)');

  const handleSlideChange = (slide) => {
    const data = {
      elementType: 'SECTION',
      position: `CAROUSEL - SLIDE ${slide}`,
    };

    trackEvent({ action: 'elementViewed', data });
  };

  const swiperSettings = {
    spaceBetween: 16,
    slidesPerView: 1,
    centeredSlides: true,
    pagination: {
      clickable: true,
      el: '.swiper-pagination',
    },
    navigation: {
      nextEl: '.swiper-button-next',
      prevEl: '.swiper-button-prev',
    },
    modules: [Pagination, Navigation],
  };

  return (
    <AddressCheckOfferPlacementStyled>
      <div className="offer-placement__container">
        <h1>{title}</h1>
        <div className="address-check-offer-tiles__container">
          {isMobile ? (
            <div className="swiper-container">
              <Swiper
                ref={swiperRef}
                onSlideChange={(swiper) => handleSlideChange(swiper.activeIndex + 1)}
                modules={[Pagination, Navigation]}
                {...swiperSettings}
              >
                {tiles?.map((tile) => {
                  const Icon = tile?.icon ? iconMap[tile?.icon] : null;
                  return (
                    <SwiperSlide key={tile.heading}>
                      <div className="address-check-offer-tile">
                        <span className="address-check-offer-tile__icon">{Icon && <Icon />}</span>
                        <h4 className="address-check-offer-tile__heading">{tile?.heading}</h4>
                        <p className="address-check-offer-tile__copy">{tile?.copy}</p>
                      </div>
                    </SwiperSlide>
                  );
                })}
              </Swiper>
              <div className="swiper-navigation">
                <div className="swiper-button-prev" />
                <div className="swiper-pagination" />
                <div className="swiper-button-next" />
              </div>
            </div>
          ) : (
            tiles?.map((tile) => {
              const Icon = tile?.icon ? iconMap[tile?.icon] : null;
              return (
                <div className="address-check-offer-tile" key={tile.heading}>
                  <p className="address-check-offer-tile__icon">{Icon && <Icon />}</p>
                  <h4 className="address-check-offer-tile__heading">{tile?.heading}</h4>
                  <p className="address-check-offer-tile__copy">
                    {tile?.copy}
                    {tile?.tooltip && (
                      <Tooltip
                        position="top-start"
                        theme="light"
                        html={
                          <div>
                            <div className="tooltip-info">{tile?.tooltip}</div>
                          </div>
                        }
                      >
                        <InfoCircle className="address-check-offer-tile__tooltip-icon" />
                      </Tooltip>
                    )}
                  </p>
                </div>
              );
            })
          )}
        </div>
      </div>
    </AddressCheckOfferPlacementStyled>
  );
};

AddressCheckOfferPlacement.displayName = 'AddressCheckOfferPlacement';

export default AddressCheckOfferPlacement;
